import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Users, 
  Search,
  Mail,
  ChevronRight,
  User,
  MessageSquare,
  UserX,
  UserCheck,
  ClipboardCheck,
  AlertCircle,
  BookOpen,
  Target,
  Pencil,
  Save
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "react-hot-toast";

const getInitials = (name) => {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
};

const StudentCard = ({ student, isInactive, unreadCount, onNavigate, onMessage, isTeacher, canEditFocus, onEditFocus }) => {
  return (
    <Card 
      className={`border-slate-100 cursor-pointer hover:border-slate-300 transition-all duration-300 card-hover ${
        isInactive ? 'opacity-75 bg-slate-50' : ''
      }`}
      onClick={() => onNavigate(`/students/${student.user_id}`)}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className={`w-12 h-12 ${isInactive ? 'grayscale' : ''}`}>
                <AvatarImage src={student.picture} />
                <AvatarFallback className={isInactive ? 'bg-slate-200 text-slate-500' : 'bg-amber-100 text-amber-700'}>
                  {getInitials(student.name)}
                </AvatarFallback>
              </Avatar>
              {unreadCount > 0 && !isInactive && (
                <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className={`font-semibold text-lg ${isInactive ? 'text-slate-500' : 'text-slate-900'}`}>
                  {student.name}
                </h3>
                {isInactive && (
                  <Badge variant="outline" className="border-orange-300 text-orange-600 bg-orange-50 text-xs">
                    Inactive
                  </Badge>
                )}
              </div>
              <p className="text-slate-500 text-sm">{student.email}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  <User className="w-3 h-3 mr-1" />
                  Student
                </Badge>
                {isTeacher && !isInactive && (
                  <>
                    {student.last_checkin_at ? (
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          student.checkin_overdue 
                            ? 'border-red-200 text-red-600 bg-red-50' 
                            : 'border-green-200 text-green-600 bg-green-50'
                        }`}
                      >
                        {student.checkin_overdue 
                          ? <AlertCircle className="w-3 h-3 mr-1" /> 
                          : <ClipboardCheck className="w-3 h-3 mr-1" />
                        }
                        Check-in {formatDistanceToNow(new Date(student.last_checkin_at), { addSuffix: true })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs border-red-200 text-red-600 bg-red-50">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        No check-ins yet
                      </Badge>
                    )}
                    {student.last_journal_at ? (
                      <Badge variant="outline" className="text-xs border-blue-200 text-blue-600 bg-blue-50">
                        <BookOpen className="w-3 h-3 mr-1" />
                        Journal {formatDistanceToNow(new Date(student.last_journal_at), { addSuffix: true })}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs border-slate-200 text-slate-500 bg-slate-50">
                        <BookOpen className="w-3 h-3 mr-1" />
                        No journals yet
                      </Badge>
                    )}
                  </>
                )}
                {student.created_at && (
                  <span className="text-xs text-slate-500">
                    Joined {format(new Date(student.created_at), 'MMM d, yyyy')}
                  </span>
                )}
              </div>
              {isTeacher && !isInactive && (
                <div
                  data-testid={`growth-focus-row-${student.user_id}`}
                  className="flex items-center gap-2 mt-2 cursor-pointer group"
                  onClick={(e) => { e.stopPropagation(); onEditFocus(student); }}
                >
                  <Target className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                  {student.growth_focus ? (
                    <span className="text-xs text-teal-700 truncate max-w-[280px]">{student.growth_focus}</span>
                  ) : (
                    <span className="text-xs text-slate-400 italic">No Growth Focus set</span>
                  )}
                  {canEditFocus && (
                    <Pencil className="w-3 h-3 text-slate-400 group-hover:text-teal-600 flex-shrink-0" />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isInactive && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onMessage(student.user_id);
                }}
                className="relative"
              >
                <Mail className="w-4 h-4 mr-1" />
                Message
                {unreadCount > 0 && (
                  <span className="ml-1 text-red-500 font-semibold">({unreadCount})</span>
                )}
              </Button>
            )}
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Students({ user }) {
  const navigate = useNavigate();
  const [activeStudents, setActiveStudents] = useState([]);
  const [inactiveStudents, setInactiveStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [focusEditTarget, setFocusEditTarget] = useState(null);
  const [focusDraft, setFocusDraft] = useState('');
  const [focusContextDraft, setFocusContextDraft] = useState('');
  const [savingFocus, setSavingFocus] = useState(false);
  const canEditFocus = user.role === 'teacher' || user.is_super_admin;

  const fetchStudents = useCallback(async () => {
    try {
      // Fetch active students (default)
      const activeRes = await axios.get(`${API}/users/students`);
      setActiveStudents(activeRes.data);
      
      // Fetch inactive students (include_inactive=true returns all, then filter)
      const allRes = await axios.get(`${API}/users/students?include_inactive=true`);
      const inactive = allRes.data.filter(s => s.is_active === false);
      setInactiveStudents(inactive);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-by-user`);
      setUnreadCounts(response.data);
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchUnreadCounts();
  }, [fetchStudents, fetchUnreadCounts]);

  const filteredActiveStudents = activeStudents.filter(student => 
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredInactiveStudents = inactiveStudents.filter(student => 
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openFocusEditor = (student) => {
    setFocusEditTarget(student);
    setFocusDraft(student.growth_focus || '');
    setFocusContextDraft(student.growth_focus_context || '');
  };

  const saveGrowthFocus = async () => {
    if (!focusEditTarget) return;
    setSavingFocus(true);
    try {
      await axios.put(`${API}/users/${focusEditTarget.user_id}/growth-focus`, {
        growth_focus: focusDraft || null,
        growth_focus_context: focusContextDraft || null
      });
      const updateList = (list) => list.map(s => s.user_id === focusEditTarget.user_id
        ? { ...s, growth_focus: focusDraft || null, growth_focus_context: focusContextDraft || null }
        : s);
      setActiveStudents(updateList);
      setInactiveStudents(updateList);
      toast.success("Growth Focus saved");
      setFocusEditTarget(null);
    } catch (error) {
      console.error("Error saving growth focus:", error);
      toast.error(error.response?.data?.detail || "Failed to save Growth Focus");
    } finally {
      setSavingFocus(false);
    }
  };

  const totalStudents = activeStudents.length + inactiveStudents.length;
  const isTeacher = user.role === 'teacher';

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="students-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              {isTeacher ? 'Students' : 'Classmates'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isTeacher 
                ? "View and manage your students' progress"
                : "Connect with your fellow classmates"
              }
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="search-students"
                placeholder={isTeacher ? "Search students..." : "Search classmates..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
          </div>
        </div>

        {/* Stats Overview - Only show for teachers */}
        {isTeacher && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Students</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{totalStudents}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Active Students</p>
                    <p className="text-3xl font-bold text-green-600 mt-1">{activeStudents.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Inactive Students</p>
                    <p className="text-3xl font-bold text-orange-600 mt-1">{inactiveStudents.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <UserX className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Search Results</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {filteredActiveStudents.length + filteredInactiveStudents.length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Search className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Active Students List */}
        <div className="space-y-4">
          {isTeacher && (
            <h2 className="font-semibold text-lg text-slate-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-600" />
              Active Students ({filteredActiveStudents.length})
            </h2>
          )}
          
          {filteredActiveStudents.length === 0 ? (
            <Card className="border-slate-100">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">
                  {searchQuery 
                    ? (isTeacher ? 'No active students match your search' : 'No classmates match your search')
                    : (isTeacher ? 'No active students enrolled yet' : 'No classmates found')
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredActiveStudents.map((student) => (
                <StudentCard 
                  key={student.user_id} 
                  student={student} 
                  isInactive={false}
                  unreadCount={unreadCounts[student.user_id] || 0}
                  onNavigate={navigate}
                  onMessage={(userId) => navigate(`/messages/${userId}`)}
                  isTeacher={isTeacher}
                  canEditFocus={canEditFocus}
                  onEditFocus={openFocusEditor}
                />
              ))}
            </div>
          )}
        </div>

        {/* Inactive Students List - Only for teachers */}
        {isTeacher && (filteredInactiveStudents.length > 0 || inactiveStudents.length > 0) && (
          <div className="space-y-4">
            <h2 className="font-semibold text-lg text-slate-500 flex items-center gap-2">
              <UserX className="w-5 h-5 text-orange-500" />
              Inactive Students ({filteredInactiveStudents.length})
            </h2>
            
            {filteredInactiveStudents.length === 0 ? (
              <Card className="border-slate-100 bg-slate-50">
                <CardContent className="py-8 text-center">
                  <p className="text-slate-500">
                    {searchQuery ? 'No inactive students match your search' : 'No inactive students'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredInactiveStudents.map((student) => (
                  <StudentCard 
                    key={student.user_id} 
                    student={student} 
                    isInactive={true}
                    unreadCount={0}
                    onNavigate={navigate}
                    onMessage={(userId) => navigate(`/messages/${userId}`)}
                    isTeacher={isTeacher}
                    canEditFocus={canEditFocus}
                    onEditFocus={openFocusEditor}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Growth Focus Quick-Edit Dialog */}
      <Dialog open={!!focusEditTarget} onOpenChange={(open) => !open && setFocusEditTarget(null)}>
        <DialogContent data-testid="growth-focus-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-700" />
              Growth Focus
            </DialogTitle>
            <DialogDescription>
              The one thing {focusEditTarget?.name} should focus on right now.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Growth Focus</p>
              <Textarea
                data-testid="growth-focus-dialog-input"
                value={focusDraft}
                onChange={(e) => setFocusDraft(e.target.value)}
                placeholder="e.g., Practicing daily prayer and journaling consistency"
                className="min-h-[70px]"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Growth Context</p>
              <p className="text-xs text-slate-500 mb-1.5">Optional — more detail on why this focus was chosen or specific examples</p>
              <Textarea
                data-testid="growth-focus-context-dialog-input"
                value={focusContextDraft}
                onChange={(e) => setFocusContextDraft(e.target.value)}
                placeholder="e.g., They mentioned struggling to find quiet time in the mornings — encourage a short 5-minute prayer before work."
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFocusEditTarget(null)} disabled={savingFocus}>
              Cancel
            </Button>
            <Button
              data-testid="growth-focus-dialog-save-btn"
              onClick={saveGrowthFocus}
              disabled={savingFocus}
              className="bg-teal-700 hover:bg-teal-800"
            >
              {savingFocus ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
